import { useState, useRef } from "react"
import { motion } from "framer-motion"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Briefcase, Handshake, GraduationCap, Heart, MessageSquare, Mail, Phone, MapPin } from "lucide-react"
import { SiWhatsapp, SiLinkedin } from "react-icons/si"
import Layout from "@/components/Layout"
import SectionHeader from "@/components/SectionHeader"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { fadeInUp, staggerContainer, staggerItem, springPresets } from "@/lib/motion"

const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  organisation: z.string().min(2, "Organisation must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().optional(),
  enquiryType: z.enum(["business", "partners", "schools", "family", "media"], {
    required_error: "Please select an enquiry type",
  }),
  message: z.string().min(10, "Message must be at least 10 characters"),
})

type FormData = z.infer<typeof formSchema>

const contactTypes = [
  {
    id: "business",
    icon: Briefcase,
    title: "Business",
    description: "Enterprise solutions and partnerships",
    color: "bg-accent",
  },
  {
    id: "partners",
    icon: Handshake,
    title: "Partners",
    description: "Distribution and collaboration opportunities",
    color: "bg-primary",
  },
  {
    id: "schools",
    icon: GraduationCap,
    title: "Schools",
    description: "Education support and school solutions",
    color: "bg-chart-2",
  },
  {
    id: "family",
    icon: Heart,
    title: "Family Access",
    description: "Personal subscriptions and family plans",
    color: "bg-accent",
  },
]

const contactChannels = [
  {
    icon: SiWhatsapp,
    title: "WhatsApp",
    value: "+27 XX XXX XXXX",
    href: "https://wa.me/27XXXXXXXXX",
    color: "text-accent",
  },
  {
    icon: Mail,
    title: "Email",
    value: "hello@sammo.co.za",
    href: "mailto:hello@sammo.co.za",
    color: "text-accent",
  },
  {
    icon: Phone,
    title: "Phone",
    value: "+27 XX XXX XXXX",
    href: "tel:+27XXXXXXXXX",
    color: "text-primary",
  },
  {
    icon: SiLinkedin,
    title: "LinkedIn",
    value: "Connect with us",
    href: "https://linkedin.com/company/sammo",
    color: "text-primary",
  },
]

export default function Contact() {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const formRef = useRef<HTMLDivElement>(null)

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      organisation: "",
      email: "",
      phone: "",
      enquiryType: undefined,
      message: "",
    },
  })

  const onSubmit = (data: FormData) => {
    setIsSubmitted(true)
    form.reset()
    setTimeout(() => setIsSubmitted(false), 5000)
  }

  const handleContactTypeClick = (type: string) => {
    form.setValue("enquiryType", type as FormData["enquiryType"])
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  return (
    <Layout>
      <div className="min-h-screen">
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Let's explore how Sammo can help
              </h1>
              <p className="text-xl text-muted-foreground">
                Whether you're a business, partner, school, or family — we're here to support you
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto"
            >
              {contactTypes.map((type) => {
                const Icon = type.icon
                return (
                  <motion.div key={type.id} variants={staggerItem}>
                    <Card
                      className="p-6 cursor-pointer transition-all hover:shadow-lg group"
                      onClick={() => handleContactTypeClick(type.id)}
                    >
                      <div className={`w-12 h-12 rounded-xl ${type.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">{type.title}</h3>
                      <p className="text-sm text-muted-foreground">{type.description}</p>
                    </Card>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </section>

        <section className="py-16" ref={formRef}>
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="max-w-2xl mx-auto"
            >
              <Card className="p-8">
                <div className="mb-8 text-center">
                  <h2 className="text-3xl font-bold mb-2">Get in touch</h2>
                  <p className="text-muted-foreground">Fill out the form below and we'll respond within 1 business day</p>
                </div>

                {isSubmitted && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={springPresets.gentle}
                    className="mb-6 p-4 bg-accent/10 border border-accent rounded-lg text-center"
                  >
                    <p className="text-accent font-medium">Thank you! We'll be in touch soon.</p>
                  </motion.div>
                )}

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Your full name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="organisation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Organisation/Company *</FormLabel>
                          <FormControl>
                            <Input placeholder="Your organisation" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email *</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="your@email.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone (optional)</FormLabel>
                            <FormControl>
                              <Input type="tel" placeholder="+27 XX XXX XXXX" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="enquiryType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Enquiry Type *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select enquiry type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="business">Business</SelectItem>
                              <SelectItem value="partners">Partners</SelectItem>
                              <SelectItem value="schools">Schools</SelectItem>
                              <SelectItem value="family">Family Access</SelectItem>
                              <SelectItem value="media">Media</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Message *</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Tell us about your needs..."
                              className="min-h-[150px] resize-none"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button type="submit" size="lg" className="w-full">
                      <MessageSquare className="w-5 h-5 mr-2" />
                      Send Message
                    </Button>
                  </form>
                </Form>
              </Card>
            </motion.div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <SectionHeader
              headline="Other ways to reach us"
              subheadline="We respond within 1 business day"
              align="center"
              className="mb-12"
            />

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto"
            >
              {contactChannels.map((channel) => {
                const Icon = channel.icon
                return (
                  <motion.div key={channel.title} variants={staggerItem}>
                    <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                      <a
                        href={channel.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <div className="flex justify-center mb-4">
                          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                            <Icon className={`w-6 h-6 ${channel.color}`} />
                          </div>
                        </div>
                        <h3 className="font-semibold mb-2">{channel.title}</h3>
                        <p className="text-sm text-muted-foreground">{channel.value}</p>
                      </a>
                    </Card>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="max-w-3xl mx-auto text-center"
            >
              <MapPin className="w-12 h-12 mx-auto mb-6 opacity-80" />
              <h2 className="text-4xl font-bold mb-4">Proudly built for Africa</h2>
              <p className="text-xl opacity-90">
                Creating practical support that works for African families, businesses, and communities
              </p>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  )
}